import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

type Variant = 'fade-up' | 'fade' | 'scale' | 'slide-right' | 'slide-left';

interface RevealProps {
  children: ReactNode;
  variant?: Variant;
  delay?: number;
  duration?: number;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
  once?: boolean;
  threshold?: number;
}

const initialStyle: Record<Variant, CSSProperties> = {
  'fade-up': { opacity: 0, transform: 'translate3d(0, 24px, 0)' },
  fade: { opacity: 0 },
  scale: { opacity: 0, transform: 'scale(0.96)' },
  'slide-right': { opacity: 0, transform: 'translate3d(-28px, 0, 0)' },
  'slide-left': { opacity: 0, transform: 'translate3d(28px, 0, 0)' },
};

const visibleStyle: CSSProperties = {
  opacity: 1,
  transform: 'translate3d(0, 0, 0) scale(1)',
};

export function Reveal({
  children,
  variant = 'fade-up',
  delay = 0,
  duration = 700,
  className,
  as: Tag = 'div',
  once = true,
  threshold = 0.15,
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [once, threshold]);

  const style: CSSProperties = {
    transition: `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
    willChange: 'opacity, transform',
    ...(visible ? visibleStyle : initialStyle[variant]),
  };

  const Component = Tag as any;
  return (
    <Component ref={ref} style={style} className={className}>
      {children}
    </Component>
  );
}
